import { chartPalette } from '@techstack/ui';
import React, { useState, useEffect, useRef } from 'react';
import {
  Send, 
  Bot, 
  User, 
  Copy, 
  Check, 
  AlertCircle, 
  Loader2, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Building2,
  Building,
  PieChart as PieChartIcon,
  Clock,
  Sparkles,
  BarChart3,
  Activity,
  Brain,
  MessageSquare,
  Zap,
  Database,
  Search,
  FileText,
  DollarSign,
  Package,
  ShoppingCart,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Settings,
  HelpCircle,
  Info,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  X,
  Lightbulb,
  Shield,
  Eye,
  EyeOff,
  Plus,
  History
} from 'lucide-react';

import {
  BarChart as RechartsBar, 
  Bar, 
  LineChart, 
  Line, 
  PieChart,
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

import apiClient from '../../lib/apiClient';
import './GenAIDashboard.css';

// SpendTreemap Component
const SpendTreemap = ({ data, title, height, onCellClick }) => (
  <div className="genai-treemap-container" style={{ height }}>
    <h3 className="genai-treemap-title">{title}</h3>
    <div className="genai-treemap-grid">
      {data.slice(0, 9).map((item, i) => (
        <div
          key={i}
          onClick={() => onCellClick(item)}
          className="genai-treemap-cell"
          style={{
            opacity: 0.5 + (item.total_spend / data[0].total_spend) * 0.5,
            height: 60 + (item.total_spend / data[0].total_spend) * 40
          }}
        >
          <div className="genai-treemap-cell-name">{item.name || item.category}</div>
          <div className="genai-treemap-cell-value">${(item.total_spend / 1000000).toFixed(1)}M</div>
        </div>
      ))}
    </div>
  </div>
);

// Error State Component
const ErrorState = ({ error, onRetry }) => (
  <div className="genai-error-state">
    <AlertCircle className="genai-error-icon" />
    <h3 className="genai-error-title">Error Loading Data</h3>
    <p className="genai-error-message">{error}</p>
    {onRetry && (
      <button onClick={onRetry} className="genai-error-retry-btn">
        Try Again
      </button>
    )}
  </div>
);

// AI Analysis Panel Component
const AIAnalysisPanel = ({ query, data, isLoading, analysis, error }) => {
  const [activeTab, setActiveTab] = useState('insights');
  
  const tabs = [
    { id: 'insights', label: 'Insights', icon: Lightbulb, color: 'purple' },
    { id: 'anomalies', label: 'Anomalies', icon: AlertTriangle, color: 'orange' },
    { id: 'predictions', label: 'Predictions', icon: TrendingUp, color: 'blue' },
    { id: 'recommendations', label: 'Actions', icon: Target, color: 'green' }
  ];

  if (error) {
    return (
      <div className="genai-analysis-panel">
        <ErrorState error={error} />
      </div>
    );
  }

  if (!analysis && !isLoading) {
    return (
      <div className="genai-analysis-panel">
        <div className="genai-analysis-empty">
          <Brain className="genai-analysis-empty-icon" />
          <p className="genai-analysis-empty-text">Run a query to see AI analysis</p>
          <p className="genai-analysis-empty-subtext">Deep analysis mode required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="genai-analysis-panel">
      <div className="genai-analysis-header">
        <div className="genai-analysis-title-group">
          <Brain className="genai-analysis-icon" />
          <h3 className="genai-analysis-title">AI Deep Analysis</h3>
        </div>
        {isLoading && (
          <div className="genai-analysis-loading">
            <Activity className="genai-analysis-loading-icon" />
            <span>Analyzing...</span>
          </div>
        )}
      </div>

      <div className="genai-analysis-tabs">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`genai-analysis-tab ${activeTab === tab.id ? 'active' : ''} ${tab.color}`}
            >
              <IconComponent className="genai-tab-icon" />
              {tab.label}
              {analysis?.[tab.id]?.length > 0 && (
                <span className="genai-tab-badge">
                  {Array.isArray(analysis[tab.id]) ? analysis[tab.id].length : '•'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="genai-analysis-content">
        {activeTab === 'insights' && (
          <div className="genai-insights-list">
            {analysis?.insights?.map((insight, idx) => (
              <div 
                key={idx}
                className={`genai-insight-card ${insight.impact}`}
              >
                <div className="genai-insight-content">
                  <h4 className="genai-insight-title">{insight.title}</h4>
                  <p className="genai-insight-text">{insight.insight}</p>
                  {insight.actionable && (
                    <div className="genai-insight-action">
                      <Lightbulb className="genai-insight-action-icon" />
                      <span>{insight.recommendation}</span>
                    </div>
                  )}
                </div>
                <span className="genai-insight-confidence">
                  {Math.round((insight.confidence || 0) * 100)}%
                </span>
              </div>
            ))}
            {(!analysis?.insights || analysis.insights.length === 0) && !isLoading && (
              <div className="genai-analysis-empty">
                <Lightbulb className="genai-analysis-empty-icon" />
                <p>No insights available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'anomalies' && (
          <div className="genai-anomalies-list">
            {analysis?.anomalies?.map((anomaly, idx) => (
              <div key={idx} className="genai-anomaly-card">
                <AlertTriangle className="genai-anomaly-icon" />
                <div className="genai-anomaly-content">
                  <h4 className="genai-anomaly-type">{anomaly.type}</h4>
                  <p className="genai-anomaly-description">{anomaly.description}</p>
                  <p className="genai-anomaly-recommendation">{anomaly.recommendation}</p>
                </div>
                <span className={`genai-severity-badge ${anomaly.severity}`}>
                  {anomaly.severity}
                </span>
              </div>
            ))}
            {(!analysis?.anomalies || analysis.anomalies.length === 0) && !isLoading && (
              <div className="genai-analysis-empty">
                <Shield className="genai-analysis-empty-icon" />
                <p>No anomalies detected</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'predictions' && (
          <div className="genai-predictions-grid">
            {analysis?.predictions && (
              <>
                {analysis.predictions.spending_forecast && (
                  <div className="genai-prediction-card forecast">
                    <h4>Next Month Forecast</h4>
                    <p className="genai-prediction-value">
                      ${((analysis.predictions.spending_forecast?.next_month || 0) / 1000000).toFixed(1)}M
                    </p>
                    <p className="genai-prediction-trend">
                      Trend: {analysis.predictions.spending_forecast?.trend || 'unknown'}
                    </p>
                    <div className="genai-prediction-confidence">
                      {Math.round((analysis.predictions.spending_forecast?.confidence || 0) * 100)}% confidence
                    </div>
                  </div>
                )}

                {analysis.predictions.opportunities?.map((opp, idx) => (
                  <div key={idx} className="genai-prediction-card opportunity">
                    <h4>Opportunity</h4>
                    <p>{opp}</p>
                  </div>
                ))}
              </>
            )}
            {!analysis?.predictions && !isLoading && (
              <div className="genai-analysis-empty">
                <TrendingUp className="genai-analysis-empty-icon" />
                <p>No predictions available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="genai-recommendations-list">
            {analysis?.recommendations?.map((rec, idx) => (
              <div key={idx} className="genai-recommendation-card">
                <Target className="genai-recommendation-icon" />
                <div className="genai-recommendation-content">
                  <div className="genai-recommendation-header">
                    <h4>{rec.action}</h4>
                    <span className={`genai-priority-badge ${rec.priority}`}>
                      {rec.priority}
                    </span>
                  </div>
                  {rec.estimatedSavings && (
                    <p className="genai-recommendation-savings">
                      Potential savings: {rec.estimatedSavings}
                    </p>
                  )}
                  <p className="genai-recommendation-effort">
                    Effort: {rec.effort}
                  </p>
                </div>
              </div>
            ))}
            {(!analysis?.recommendations || analysis.recommendations.length === 0) && !isLoading && (
              <div className="genai-analysis-empty">
                <Target className="genai-analysis-empty-icon" />
                <p>No recommendations available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main GenAIDashboard Component
const GenAIDashboard = ({
  supabase,
  selectedDepartment = null,
  departmentName = null
}) => {
  // Helper functions for department resolution
  const getDepartmentIdentifier = () => {
    if (!selectedDepartment) return null;
    
    if (typeof selectedDepartment === 'object') {
      return selectedDepartment.acronym || 
             selectedDepartment.department_code || 
             selectedDepartment.official_name ||
             selectedDepartment.common_name;
    }
    
    return selectedDepartment;
  };

  const getDepartmentDisplayName = () => {
    if (!selectedDepartment) return 'All Departments';
    
    if (typeof selectedDepartment === 'object') {
      return selectedDepartment.common_name || 
             selectedDepartment.official_name || 
             selectedDepartment.acronym ||
             'Selected Department';
    }
    
    return departmentName || selectedDepartment;
  };

  const departmentId = getDepartmentIdentifier();
  const displayDepartment = getDepartmentDisplayName();
  const isDepartmentMode = departmentId && departmentId !== 'all';

  // State variables
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState('gpt-4');
  const [copiedCode, setCopiedCode] = useState(null);
  const [queryResults, setQueryResults] = useState(null);
  const [procurementStats, setProcurementStats] = useState(null);
  const [showSmartQueries, setShowSmartQueries] = useState(true);
  const [analysisMode, setAnalysisMode] = useState('smart');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [showSQL, setShowSQL] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  const messagesEndRef = useRef(null);
  // Session ID for grouping messages into conversations
  const sessionIdRef = useRef(crypto.randomUUID());

  // Load conversation history on mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  // Load chat history from API
  const loadChatHistory = async () => {
    try {
      setChatHistoryLoading(true);
      const data = await apiClient.get('/api/ai-conversations');
      const conversations = data.conversations || [];

      // Group by date
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      const grouped = conversations.map(conv => {
        const updatedAt = new Date(conv.updated_at || conv.created_at);
        let dateGroup = 'older';
        if (updatedAt >= today) {
          dateGroup = 'today';
        } else if (updatedAt >= yesterday) {
          dateGroup = 'yesterday';
        } else if (updatedAt >= weekAgo) {
          dateGroup = 'week';
        }
        return {
          ...conv,
          date: dateGroup,
          active: conv.id === currentConversationId,
        };
      });

      setChatHistory(grouped);
    } catch (err) {
      console.error('Failed to load chat history:', err);
    } finally {
      setChatHistoryLoading(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      const data = await apiClient.get(`/api/ai-conversations/${conversationId}`);

      // Set the conversation's messages
      const loadedMessages = (data.messages || []).map(m => ({
        role: m.role,
        content: m.content,
        ui_schema: m.ui_schema,
        tools_used: m.tools_used,
      }));

      setMessages(loadedMessages);
      setCurrentConversationId(conversationId);

      // Update session ID to continue this conversation
      sessionIdRef.current = data.conversation?.session_id || conversationId;

      // Mark this conversation as active in the sidebar
      setChatHistory(prev => prev.map(c => ({
        ...c,
        active: c.id === conversationId,
      })));

      // Clear any previous results
      setQueryResults(null);
      setAiAnalysis(null);
      setShowSmartQueries(loadedMessages.length === 0);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Start a new chat
  const startNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    sessionIdRef.current = crypto.randomUUID();
    setQueryResults(null);
    setAiAnalysis(null);
    setShowSmartQueries(true);

    // Clear active state in sidebar
    setChatHistory(prev => prev.map(c => ({
      ...c,
      active: false,
    })));
  };
  
  // Smart query templates
  const smartQueryTemplates = [
    {
      query: "Show spending by category",
      icon: PieChartIcon,
      color: "blue",
      description: isDepartmentMode
        ? `${displayDepartment}'s category breakdown`
        : "View spending breakdown by category"
    },
    {
      query: "Show top vendors",
      icon: Building2,
      color: "green",
      description: isDepartmentMode
        ? `Top vendors for ${displayDepartment}`
        : "View top vendors by spend"
    },
    {
      query: "Recent purchases",
      icon: Clock,
      color: "purple",
      description: isDepartmentMode
        ? `Recent ${displayDepartment} purchases`
        : "View recent procurement activity"
    },
    {
      query: "High value purchases",
      icon: TrendingUp,
      color: "orange",
      description: isDepartmentMode
        ? `${displayDepartment}'s large purchases`
        : "View purchases over $100k"
    }
  ];

  // The shared ramp, not an eighth private one. This was its own array of six
  // hues plus two greys used as filler — the greys meant nothing here, and a
  // grey slot in a series palette is indistinguishable from the residual.
  const CHART_COLORS = chartPalette;

  // Reset messages and analysis when department changes
  useEffect(() => {
    setMessages([]);
    setQueryResults(null);
    setAiAnalysis(null);
    setShowSmartQueries(true);
    setShowSQL({});
  }, [selectedDepartment]);

  // Wake up backend on mount
  useEffect(() => {
    const wakeUpBackend = async () => {
      try {
        console.log('🔄 Attempting to wake up backend...');
        await apiClient.get('/health');
        console.log('✅ Backend is now awake');
      } catch (error) {
        // Fallback ping
        apiClient.get('/api/departments').catch(() => {});
      }
    };

    wakeUpBackend();
  }, []);

  // Load procurement stats when component mounts or department changes
  useEffect(() => {
    loadProcurementStats();
  }, [selectedDepartment]);

  // Load real procurement stats
  const loadProcurementStats = async () => {
    try {
      const params = isDepartmentMode
        ? `?department=${encodeURIComponent(selectedDepartment)}`
        : '';

      const stats = await apiClient.get(`/api/ai/stats${params}`);
      setProcurementStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Perform deep AI analysis when mode changes or new results arrive
  useEffect(() => {
    if (analysisMode === 'deep' && queryResults?.data && queryResults.data.length > 0) {
      performDeepAnalysis(messages[messages.length - 2]?.content || '', queryResults.data);
    }
  }, [analysisMode, queryResults]);

  // Deep AI Analysis function
  const performDeepAnalysis = async (query, data) => {
    setAnalysisLoading(true);
    setAiAnalysis(null);
    setAnalysisError(null);

    try {
      const result = await apiClient.post('/api/ai-analytics/analyze', {
        query: query || 'Analyze this procurement data',
        data: data.slice(0, 100),
        context: {
          department: selectedDepartment,
          departmentName: displayDepartment
        }
      });

      if (result.analysis) {
        setAiAnalysis({
          insights: result.analysis.insights || [],
          anomalies: result.analysis.anomalies || [],
          predictions: result.analysis.predictions || {},
          recommendations: result.analysis.recommendations || []
        });
      }

      setAnalysisLoading(false);

    } catch (error) {
      console.error('AI Analysis error:', error);
      setAnalysisError(error.message);
      setAnalysisLoading(false);
    }
  };

  // Send message function
  const sendMessage = async (queryText = inputMessage) => {
    if (!queryText.trim()) return;

    const userMessage = {
      role: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setInputMessage('');
    setQueryError(null);

    try {
      const requestBody = {
        query: queryText,
        provider: provider || 'gpt-4',
        sessionId: sessionIdRef.current,
      };

      if (isDepartmentMode && departmentId) {
        requestBody.department = departmentId;
      }

      console.log('🚀 Sending AI query:', {
        url: '/api/ai/query',
        department: requestBody.department || 'all',
        provider: requestBody.provider,
        sessionId: requestBody.sessionId
      });

      const result = await apiClient.post('/api/ai/query', requestBody);
      console.log('✅ Query successful:', result);

      let messageContent = `Found ${result.resultCount || 0} results`;
      if (isDepartmentMode && !messageContent.includes(displayDepartment)) {
        messageContent += ` for ${displayDepartment}`;
      }

      const assistantMessage = {
        role: 'assistant',
        content: messageContent,
        sql: result.sql,
        data: result.data,
        visualization: result.visualization,
        suggestions: result.suggestions,
        responseTime: result.responseTime,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setQueryResults(result);

      // Refresh chat history to show this conversation (if it was persisted)
      loadChatHistory();

    } catch (error) {
      console.error('🔴 Query error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: `❌ ${error.message}`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setQueryError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Render visualization with dark theme
  const renderVisualization = (message) => {
    if (!message.data || message.data.length === 0) return null;

    const vizType = message.visualization?.type || 'table';

    switch (vizType) {
      case 'pie':
        const pieData = message.data.map((item, idx) => ({
          name: item.category || item.name || 'Unknown',
          value: parseFloat(item.total_amount || item.amount || item.value || 0),
          fill: CHART_COLORS[idx % CHART_COLORS.length]
        }));

        return (
          <div className="genai-viz-container">
            <h3 className="genai-viz-title">Spending by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `$${value.toLocaleString()}`}
                  contentStyle={{ backgroundColor: '#1e1e2a', border: '1px solid rgba(255,255,255,0.1)' }}
                  labelStyle={{ color: '#ffffff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );

      case 'bar':
        const barData = message.data.slice(0, 10).map(item => ({
          name: item.vendor || item.supplier_name || item.name || 'Unknown',
          value: parseFloat(item.total_amount || item.amount || item.total || 0)
        }));

        return (
          <div className="genai-viz-container">
            <h3 className="genai-viz-title">Top Results</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBar data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={11}
                  tick={{ fill: '#a0a0b0' }}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  tick={{ fill: '#a0a0b0' }}
                />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                  contentStyle={{ backgroundColor: '#1e1e2a', border: '1px solid rgba(255,255,255,0.1)' }}
                  labelStyle={{ color: '#ffffff' }}
                />
                <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </RechartsBar>
            </ResponsiveContainer>
          </div>
        );

      case 'line':
        const lineData = message.data.map((item, i) => ({
          month: item.month || item.created_at || `Month ${i + 1}`,
          value: parseFloat(item.total_spend || item.amount || item.total || 0)
        }));

        return (
          <div className="genai-viz-container">
            <h3 className="genai-viz-title">Trend Analysis</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#a0a0b0' }}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  tick={{ fill: '#a0a0b0' }}
                />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                  contentStyle={{ backgroundColor: '#1e1e2a', border: '1px solid rgba(255,255,255,0.1)' }}
                  labelStyle={{ color: '#ffffff' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', r: 5 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return (
          <div className="genai-table-container">
            <table className="genai-data-table">
              <thead>
                <tr>
                  {Object.keys(message.data[0]).map(key => (
                    <th key={key}>
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {message.data.slice(0, 10).map((row, i) => (
                  <tr key={i}>
                    {Object.entries(row).map(([key, value], j) => (
                      <td key={j} data-label={key.replace(/_/g, ' ')}>
                        {typeof value === 'number' && (key.includes('amount') || key.includes('total') || key.includes('spend'))
                          ? `$${parseFloat(value).toLocaleString()}`
                          : value instanceof Date
                          ? new Date(value).toLocaleDateString()
                          : value || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {message.data.length > 10 && (
              <p className="genai-table-footer">
                Showing 10 of {message.data.length} results
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="genai-dashboard">
      {/* Sidebar */}
      <aside className={`genai-sidebar ${showSidebar ? '' : 'collapsed'}`}>
        <div className="genai-sidebar-header">
          <div className="genai-sidebar-logo">
            <div className="genai-logo-icon">AI</div>
            <span className="genai-logo-text">GenAI Assistant</span>
          </div>
        </div>

        <button className="genai-new-chat-btn" onClick={startNewChat}>
          <Plus className="w-4 h-4" />
          New chat
        </button>

        <div className="genai-chat-history">
          {chatHistoryLoading ? (
            <div className="genai-history-loading">
              <Loader2 className="genai-history-spinner" />
              <span>Loading history...</span>
            </div>
          ) : chatHistory.length === 0 ? (
            <div className="genai-history-empty">
              <MessageSquare className="genai-history-empty-icon" />
              <span>No conversations yet</span>
            </div>
          ) : (
            <>
              {chatHistory.filter(chat => chat.date === 'today').length > 0 && (
                <div className="genai-history-section">
                  <div className="genai-history-label">Today</div>
                  {chatHistory
                    .filter(chat => chat.date === 'today')
                    .map(chat => (
                      <div
                        key={chat.id}
                        className={`genai-history-item ${chat.active ? 'active' : ''}`}
                        onClick={() => loadConversation(chat.id)}
                        title={chat.title || 'Untitled'}
                      >
                        <MessageSquare className="genai-history-icon" />
                        <span className="genai-history-title">
                          {chat.title || 'Untitled conversation'}
                        </span>
                      </div>
                    ))}
                </div>
              )}

              {chatHistory.filter(chat => chat.date === 'yesterday').length > 0 && (
                <div className="genai-history-section">
                  <div className="genai-history-label">Yesterday</div>
                  {chatHistory
                    .filter(chat => chat.date === 'yesterday')
                    .map(chat => (
                      <div
                        key={chat.id}
                        className={`genai-history-item ${chat.active ? 'active' : ''}`}
                        onClick={() => loadConversation(chat.id)}
                        title={chat.title || 'Untitled'}
                      >
                        <MessageSquare className="genai-history-icon" />
                        <span className="genai-history-title">
                          {chat.title || 'Untitled conversation'}
                        </span>
                      </div>
                    ))}
                </div>
              )}

              {chatHistory.filter(chat => chat.date === 'week').length > 0 && (
                <div className="genai-history-section">
                  <div className="genai-history-label">Previous 7 Days</div>
                  {chatHistory
                    .filter(chat => chat.date === 'week')
                    .map(chat => (
                      <div
                        key={chat.id}
                        className={`genai-history-item ${chat.active ? 'active' : ''}`}
                        onClick={() => loadConversation(chat.id)}
                        title={chat.title || 'Untitled'}
                      >
                        <MessageSquare className="genai-history-icon" />
                        <span className="genai-history-title">
                          {chat.title || 'Untitled conversation'}
                        </span>
                      </div>
                    ))}
                </div>
              )}

              {chatHistory.filter(chat => chat.date === 'older').length > 0 && (
                <div className="genai-history-section">
                  <div className="genai-history-label">Older</div>
                  {chatHistory
                    .filter(chat => chat.date === 'older')
                    .map(chat => (
                      <div
                        key={chat.id}
                        className={`genai-history-item ${chat.active ? 'active' : ''}`}
                        onClick={() => loadConversation(chat.id)}
                        title={chat.title || 'Untitled'}
                      >
                        <MessageSquare className="genai-history-icon" />
                        <span className="genai-history-title">
                          {chat.title || 'Untitled conversation'}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="genai-main">
        {/* Header */}
        <header className="genai-header">
          <div className="genai-header-content">
            <div className="genai-header-left">
              <h1 className="genai-header-title">
                {isDepartmentMode ? `${displayDepartment} Analytics` : 'AI Analytics Dashboard'}
              </h1>
              {isDepartmentMode && (
                <p className="genai-header-subtitle">Department-specific insights</p>
              )}
            </div>

            <div className="genai-header-right">
              {/* Analysis Mode Toggle */}
              <div className="genai-mode-toggle">
                <button
                  onClick={() => setAnalysisMode('smart')}
                  className={`genai-mode-btn ${analysisMode === 'smart' ? 'active' : ''}`}
                >
                  <Zap className="w-4 h-4" />
                  Smart Queries
                </button>
                <button
                  onClick={() => setAnalysisMode('deep')}
                  className={`genai-mode-btn ${analysisMode === 'deep' ? 'active' : ''}`}
                >
                  <Brain className="w-4 h-4" />
                  Deep Analysis
                </button>
              </div>

              <div className="genai-header-actions">
                <button className="genai-header-btn" title="Settings">
                  <Settings className="w-4 h-4" />
                </button>
                <button className="genai-header-btn" title="Help">
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          {procurementStats && (
            <div className="genai-stats-grid">
              <div className="genai-stat-card">
                <div className="genai-stat-content">
                  <p className="genai-stat-value">
                    {procurementStats.totalTransactions?.toLocaleString()}
                  </p>
                  <p className="genai-stat-label">
                    {isDepartmentMode ? 'Department Transactions' : 'Total Transactions'}
                  </p>
                </div>
                <Activity className="genai-stat-icon" />
              </div>
              <div className="genai-stat-card">
                <div className="genai-stat-content">
                  <p className="genai-stat-value">
                    ${(procurementStats.totalSpending / 1000000).toFixed(1)}M
                  </p>
                  <p className="genai-stat-label">
                    {isDepartmentMode ? 'Department Spending' : 'Total Spending'}
                  </p>
                </div>
                <DollarSign className="genai-stat-icon" />
              </div>
              <div className="genai-stat-card">
                <div className="genai-stat-content">
                  <p className="genai-stat-value">
                    {procurementStats.uniqueVendors?.toLocaleString()}
                  </p>
                  <p className="genai-stat-label">
                    {isDepartmentMode ? 'Vendors Used' : 'Unique Vendors'}
                  </p>
                </div>
                <Building2 className="genai-stat-icon" />
              </div>
              <div className="genai-stat-card">
                <div className="genai-stat-content">
                  <p className="genai-stat-value">
                    {procurementStats.activeDepartments}
                  </p>
                  <p className="genai-stat-label">
                    {isDepartmentMode ? 'Active Department' : 'Active Departments'}
                  </p>
                </div>
                <Target className="genai-stat-icon" />
              </div>
            </div>
          )}
        </header>

        <div className="genai-content-area">
          {/* Main Chat Column */}
          <div className="genai-chat-container">
            {/* Smart Query Templates */}
            {showSmartQueries && (
              <div className="genai-smart-queries">
                <div className="genai-smart-queries-header">
                  <h3>Quick Queries</h3>
                  <button
                    onClick={() => setShowSmartQueries(false)}
                    className="genai-close-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="genai-smart-queries-grid">
                  {smartQueryTemplates.map((template, index) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setInputMessage(template.query);
                          sendMessage(template.query);
                        }}
                        className={`genai-smart-query-btn ${template.color}`}
                        disabled={isLoading}
                      >
                        <Icon className="genai-smart-query-icon" />
                        <span className="genai-smart-query-text">{template.query}</span>
                        <span className="genai-smart-query-desc">{template.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="genai-messages-container">
              {messages.length === 0 ? (
                <div className="genai-empty-state">
                  <Brain className="genai-empty-icon" />
                  <h3 className="genai-empty-title">Start with a query</h3>
                  <p className="genai-empty-text">Try clicking a quick query above or type your own</p>
                </div>
              ) : (
                <div className="genai-messages-inner">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`genai-message ${message.role}`}
                    >
                      <div className="genai-message-avatar">
                        {message.role === 'user' ? 'U' : 'AI'}
                      </div>
                      <div className="genai-message-content">
                        {message.role === 'user' ? (
                          <p>{message.content}</p>
                        ) : (
                          <>
                            {message.responseTime && (
                              <span className="genai-response-time">
                                {message.responseTime}ms
                              </span>
                            )}

                            {message.isError ? (
                              <div className="genai-error-message">
                                <p>{message.content}</p>
                              </div>
                            ) : (
                              <p className="genai-message-text">{message.content}</p>
                            )}

                            {message.sql && (
                              <div className="genai-sql-container">
                                <div className="genai-sql-header">
                                  <span>SQL Query</span>
                                  <div className="genai-sql-actions">
                                    <button
                                      onClick={() => setShowSQL(prev => ({
                                        ...prev,
                                        [index]: !prev[index]
                                      }))}
                                      className="genai-sql-action"
                                    >
                                      {showSQL[index] ? (
                                        <>
                                          <EyeOff className="w-3 h-3" />
                                          Hide
                                        </>
                                      ) : (
                                        <>
                                          <Eye className="w-3 h-3" />
                                          Show
                                        </>
                                      )}
                                    </button>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(message.sql);
                                        setCopiedCode(index);
                                        setTimeout(() => setCopiedCode(null), 2000);
                                      }}
                                      className="genai-sql-action"
                                    >
                                      {copiedCode === index ? (
                                        <>
                                          <Check className="w-3 h-3" />
                                          Copied
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3" />
                                          Copy
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                                {showSQL[index] && (
                                  <pre className="genai-sql-code">
                                    <code>{message.sql}</code>
                                  </pre>
                                )}
                              </div>
                            )}

                            {renderVisualization(message)}

                            {message.suggestions && message.suggestions.length > 0 && (
                              <div className="genai-suggestions">
                                <p className="genai-suggestions-label">Follow-up queries:</p>
                                <div className="genai-suggestions-list">
                                  {message.suggestions.map((suggestion, i) => (
                                    <button
                                      key={i}
                                      onClick={() => {
                                        setInputMessage(suggestion);
                                        sendMessage(suggestion);
                                      }}
                                      className="genai-suggestion-btn"
                                      disabled={isLoading}
                                    >
                                      {suggestion}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="genai-input-section">
              <div className="genai-input-wrapper">
                <textarea
                  className="genai-chat-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={
                    isDepartmentMode
                      ? `Ask about ${displayDepartment}'s data...`
                      : "Ask about your procurement data..."
                  }
                  disabled={isLoading}
                  rows="1"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={isLoading || !inputMessage.trim()}
                  className="genai-send-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* AI Analysis Panel - Only show in deep mode */}
          {analysisMode === 'deep' && (
            <div className="genai-analysis-sidebar">
              <AIAnalysisPanel 
                query={messages[messages.length - 2]?.content}
                data={queryResults?.data}
                isLoading={analysisLoading}
                analysis={aiAnalysis}
                error={analysisError}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GenAIDashboard;
export { AIAnalysisPanel };